/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/AlertBanner/AlertBanner.css';
import { Button, Icon } from '@adobe-commerce/elsie/components';
import { Close } from '@adobe-commerce/elsie/icons';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface AlertBannerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'icon' | 'action'> {
  variant: 'brand' | 'neutral' | 'success' | 'warning';
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  message: VNode;
  onDismiss: () => void;
  action?: {
    label: string;
    onClick: (event: Event) => void;
  };
}

export const AlertBanner: FunctionComponent<AlertBannerProps> = ({
  className,
  variant,
  icon,
  message,
  onDismiss,
  action,
  ...props
}) => {
  const translations = useText({
    dismiss: 'Dropin.InlineAlert.dismissLabel',
  });

  return (
    <div
      {...props}
      className={classes([
        className,
        'dropin-alert-banner',
        `dropin-alert-banner--${variant}`,
      ])}
    >
      <div className={'dropin-alert-banner__content'}>
        {icon && (
          <VComponent
            node={icon}
            aria-hidden="true"
            className={'dropin-alert-banner__icon'}
          />
        )}

        {/* Message */}
        <VComponent
          node={message}
          className={classes(['dropin-alert-banner__message'])}
        />
      </div>

      <div className={'dropin-alert-banner__actions'}>
        {action && (
          <Button
            variant="tertiary"
            className={'dropin-alert-banner__action'}
            onClick={action.onClick}
            aria-label={action.label}
          >
            {action.label}
          </Button>
        )}

        <Button
          icon={<Icon source={Close} size="24" stroke="2" />}
          className="dropin-alert-banner__dismiss-button"
          variant="primary"
          onClick={onDismiss}
          aria-label={translations.dismiss}
        />
      </div>
    </div>
  );
};
